import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaCheckCircle,
  FaCreditCard,
  FaDownload,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFilter,
  FaChartBar,
  FaHourglassHalf,
  FaMoneyBillWave,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTags,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import MdlAddExpense from "../dialog/MdlAddExpense";
import MdlManageDaybookMaster from "../dialog/MdlManageDaybookMaster";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function formatCurrency(amount) {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

//Created By Cursor
function formatCurrencyCompact(amount) {
  const num = parseFloat(amount) || 0;
  if (Math.abs(num) >= 10000000) {
    return `₹${(num / 10000000).toFixed(2)} Cr`;
  }
  if (Math.abs(num) >= 100000) {
    return `₹${(num / 100000).toFixed(2)} L`;
  }
  return formatCurrency(num);
}

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor
function buildSessionMonths(startRaw, endRaw) {
  const start = parseLooseDate(startRaw);
  const end = parseLooseDate(endRaw);
  if (!start || !end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

//Created By Cursor
function formatMonthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

//Created By Cursor
function getCurrentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

//Created By Cursor — 0 = least (green), 1 = most (red)
function spendHeatStyle(ratio) {
  const t = Math.max(0, Math.min(1, Number(ratio) || 0));
  const mix = (a, b) => Math.round(a + (b - a) * t);
  const r = mix(22, 220);
  const g = mix(163, 38);
  const b = mix(74, 38);
  const border = `rgb(${r}, ${g}, ${b})`;
  const softR = mix(220, 254);
  const softG = mix(252, 226);
  const softB = mix(231, 226);
  return {
    "--em-month-border": border,
    "--em-month-accent": border,
    "--em-month-bg": `linear-gradient(165deg, rgb(${softR}, ${softG}, ${softB}) 0%, #ffffff 72%)`,
    "--em-month-amount": border,
  };
}

//Created By Cursor
function statusPillClass(status) {
  if (status === "Active" || status === "APPROVED") return "em-pill em-pill--ok";
  if (status === "Pending" || status === "PENDING") return "em-pill em-pill--warn";
  return "em-pill em-pill--bad";
}

//Created By Cursor
function statusLabel(status) {
  if (status === "Active") return "Active";
  if (status === "InActive") return "Inactive";
  return status || "Pending";
}

//Created By Cursor
export default function ExpenseManager() {
  const loggedIn = localStorage.getItem("token") != null;

  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStartDate, setSessionStartDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState("");
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [isManageCategoryOpen, setIsManageCategoryOpen] = useState(false);
  const [isManagePartyOpen, setIsManagePartyOpen] = useState(false);
  const [expenseList, setExpenseList] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    total_expense_cash_out: "0",
    total_expense_cash_in: "0",
    this_month_cash_out: "0",
    this_month_count: 0,
    pending_count: 0,
    monthly: [],
    by_party: [],
  });
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    if (Array.isArray(mSession) && mSession.length > 0) {
      setSessionId(mSession[0].session_id);
      setSessionName(mSession[0].session_name);
      setSessionStartDate(mSession[0].start_date || "");
      setSessionEndDate(mSession[0].end_date || "");
    }
  }, []);

  //Created By Cursor
  const fetchExpenses = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!sessionId || !schoolCode) return;
    try {
      setLoadingExpenses(true);
      const response = await axios.get(`${API_BASE()}getDaybookExpenseList.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
          limit: 10,
          offset: 0,
        },
      });
      if (response.data?.api_status === "OK") {
        setExpenseList(response.data.data || []);
      } else {
        setExpenseList([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenseList([]);
    } finally {
      setLoadingExpenses(false);
    }
  }, [sessionId]);

  //Created By Cursor
  const fetchDashboard = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!sessionId || !schoolCode) return;
    try {
      setLoadingDashboard(true);
      const response = await axios.get(`${API_BASE()}getDayBookDashboard.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
        },
      });
      if (response.data?.api_status === "OK") {
        const dashboard = response.data.dashboard || {};
        setDashboardData({
          total_expense_cash_out: dashboard.total_expense_cash_out || "0",
          total_expense_cash_in: dashboard.total_expense_cash_in || "0",
          this_month_cash_out: dashboard.this_month_cash_out || "0",
          this_month_count: Number(dashboard.this_month_count) || 0,
          pending_count: Number(dashboard.pending_count) || 0,
          monthly: Array.isArray(dashboard.monthly) ? dashboard.monthly : [],
          by_party: Array.isArray(dashboard.by_party) ? dashboard.by_party : [],
        });
      } else {
        setDashboardData({
          total_expense_cash_out: "0",
          total_expense_cash_in: "0",
          this_month_cash_out: "0",
          this_month_count: 0,
          pending_count: 0,
          monthly: [],
          by_party: [],
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setDashboardData({
        total_expense_cash_out: "0",
        total_expense_cash_in: "0",
        this_month_cash_out: "0",
        this_month_count: 0,
        pending_count: 0,
        monthly: [],
        by_party: [],
      });
    } finally {
      setLoadingDashboard(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    fetchExpenses();
    fetchDashboard();
  }, [sessionId, fetchExpenses, fetchDashboard]);

  //Created By Cursor
  const refreshAll = useCallback(async () => {
    if (!sessionId) return;
    setRefreshing(true);
    try {
      await Promise.all([fetchExpenses(), fetchDashboard()]);
    } finally {
      setRefreshing(false);
    }
  }, [sessionId, fetchExpenses, fetchDashboard]);

  const filteredExpenses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return expenseList.filter((exp) => {
      if (statusFilter === "ACTIVE") {
        if (exp.status !== "Active" && exp.status !== "APPROVED") return false;
      } else if (statusFilter === "PENDING") {
        if (exp.status === "Active" || exp.status === "APPROVED") return false;
      }
      if (!q) return true;
      const hay = [
        exp.category_name,
        exp.party_name,
        exp.payment_mode,
        exp.remark,
        exp.entry_date,
        exp.amount,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [expenseList, searchQuery, statusFilter]);

  const currentMonthData = useMemo(
    () => ({
      total: parseFloat(dashboardData.this_month_cash_out) || 0,
      count: Number(dashboardData.this_month_count) || 0,
    }),
    [dashboardData.this_month_cash_out, dashboardData.this_month_count]
  );

  const pendingCount = useMemo(
    () => Number(dashboardData.pending_count) || 0,
    [dashboardData.pending_count]
  );

  //Created By Cursor — all session months with expense totals
  const sessionMonthExpenses = useMemo(() => {
    const months = buildSessionMonths(sessionStartDate, sessionEndDate);
    const byYm = {};
    (dashboardData.monthly || []).forEach((row) => {
      if (!row?.ym) return;
      byYm[row.ym] = {
        total: parseFloat(row.total) || 0,
        count: Number(row.count) || 0,
      };
    });
    const currentYm = getCurrentYearMonth();
    return months.map((ym) => ({
      ym,
      label: formatMonthLabel(ym),
      total: byYm[ym]?.total || 0,
      count: byYm[ym]?.count || 0,
      isCurrent: ym === currentYm,
    }));
  }, [sessionStartDate, sessionEndDate, dashboardData.monthly]);

  //Created By Cursor
  const monthChartData = useMemo(() => {
    const labels = sessionMonthExpenses.map((m) => m.label);
    const values = sessionMonthExpenses.map((m) => m.total);
    const currentYm = getCurrentYearMonth();
    const colors = sessionMonthExpenses.map((m) => {
      if (m.ym > currentYm) return "rgba(148, 163, 184, 0.7)";
      if (m.isCurrent) return "rgba(3, 169, 244, 0.85)";
      return "rgba(2, 136, 209, 0.75)";
    });
    return {
      labels,
      datasets: [
        {
          label: "Expense",
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace("0.7", "1").replace("0.85", "1").replace("0.75", "1")),
          borderWidth: 1,
          borderRadius: 8,
          maxBarThickness: 36,
        },
      ],
    };
  }, [sessionMonthExpenses]);

  //Created By Cursor
  const partyChartData = useMemo(() => {
    const rows = Array.isArray(dashboardData.by_party)
      ? dashboardData.by_party
      : [];
    const palette = [
      "#0288d1",
      "#26a69a",
      "#7e57c2",
      "#ef5350",
      "#ffa726",
      "#66bb6a",
      "#42a5f5",
      "#ec407a",
      "#8d6e63",
      "#5c6bc0",
      "#26c6da",
      "#ab47bc",
    ];
    return {
      labels: rows.map((r) => r.party_name || "Unassigned"),
      datasets: [
        {
          label: "Expense",
          data: rows.map((r) => parseFloat(r.total) || 0),
          backgroundColor: rows.map((_, i) => palette[i % palette.length]),
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    };
  }, [dashboardData.by_party]);

  const monthChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => formatCurrency(ctx.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10, weight: "700" }, color: "#64748b", maxRotation: 45, minRotation: 0 },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(226, 232, 240, 0.9)" },
          ticks: {
            font: { size: 10, weight: "600" },
            color: "#94a3b8",
            callback: (v) => {
              const n = Number(v) || 0;
              if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
              if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
              return String(n);
            },
          },
        },
      },
    }),
    []
  );

  const partyChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "right",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            font: { size: 11, weight: "600" },
            color: "#475569",
            padding: 10,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const label = ctx.label || "";
              return `${label}: ${formatCurrency(ctx.parsed)}`;
            },
          },
        },
      },
      cutout: "58%",
    }),
    []
  );

  const categoryBreakdown = useMemo(() => {
    const totals = {};
    expenseList.forEach((exp) => {
      const category = exp.category_name || "Uncategorized";
      totals[category] = (totals[category] || 0) + (parseFloat(exp.amount) || 0);
    });
    const rows = Object.entries(totals)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);
    const max = rows[0]?.amount || 0;
    const totalSpend =
      parseFloat(dashboardData.total_expense_cash_out) ||
      rows.reduce((s, r) => s + r.amount, 0) ||
      1;
    return rows.map((r) => ({
      ...r,
      pct: totalSpend > 0 ? ((r.amount / totalSpend) * 100).toFixed(1) : "0.0",
      bar: max > 0 ? Math.max(6, (r.amount / max) * 100) : 0,
    }));
  }, [expenseList, dashboardData.total_expense_cash_out]);

  const topCategory = categoryBreakdown[0] || {
    name: "—",
    pct: "0.0",
    amount: 0,
  };

  //Created By Cursor — derived KPI helpers
  const kpiStats = useMemo(() => {
    const cashOut = parseFloat(dashboardData.total_expense_cash_out) || 0;
    const cashIn = parseFloat(dashboardData.total_expense_cash_in) || 0;
    const monthTotal = currentMonthData.total;
    const monthShare =
      cashOut > 0 ? Math.min(100, (monthTotal / cashOut) * 100) : 0;
    const net = cashIn - cashOut;
    const catPct = Math.min(100, parseFloat(topCategory.pct) || 0);
    return {
      cashOut,
      cashIn,
      net,
      monthShare,
      catPct,
      monthLabel: new Date().toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      }),
    };
  }, [
    dashboardData.total_expense_cash_out,
    dashboardData.total_expense_cash_in,
    currentMonthData.total,
    topCategory.pct,
  ]);

  const busy = loadingExpenses || loadingDashboard || refreshing;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sr-page stur-page em-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Expense Manager</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell em-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCreditCard />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Expense Manager</h1>
              <p className="sm-hero__sub">
                Track cash-out, categories & daybook expenses
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="stur-hero-session"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change academic session"
            >
              <FaCalendarAlt aria-hidden />
              <span className="stur-hero-session__text min-w-0">
                <span className="stur-hero-session__label">Session</span>
                <strong className="stur-hero-session__name">
                  {sessionName || "Select session"}
                </strong>
              </span>
              <FaExchangeAlt className="stur-hero-session__swap" aria-hidden />
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
              disabled={busy || !sessionId}
              onClick={refreshAll}
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="em-btn-new"
              disabled={!sessionId || busy}
              onClick={() => setIsAddExpenseOpen(true)}
            >
              <span className="em-btn-new__icon" aria-hidden>
                <FaPlus />
              </span>
              <span className="em-btn-new__label">New expense</span>
            </button>
          </div>
        </header>

        <div className="sr-body stur-body em-body">
          <div className="em-metrics em-metrics--compact" aria-label="Expense KPIs">
            <article
              role="button"
              tabIndex={0}
              className={`em-metric em-metric--out em-metric--clickable${
                loadingDashboard ? " is-loading" : ""
              }`}
              title="View all cash-out entries"
              onClick={() => {
                if (!sessionId) return;
                window.location.href =
                  "/AdminMain/ExpenseList?entry_type=CASH_OUT";
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                if (!sessionId) return;
                window.location.href =
                  "/AdminMain/ExpenseList?entry_type=CASH_OUT";
              }}
            >
              <span className="em-metric__icon" aria-hidden>
                <FaMoneyBillWave />
              </span>
              <div className="em-metric__body">
                <span className="em-metric__label">Cash out</span>
                <strong title={formatCurrency(kpiStats.cashOut)}>
                  {loadingDashboard
                    ? "…"
                    : formatCurrencyCompact(kpiStats.cashOut)}
                </strong>
                <em>
                  Net{" "}
                  <span
                    className={
                      kpiStats.net >= 0
                        ? "em-metric__trend em-metric__trend--up"
                        : "em-metric__trend em-metric__trend--down"
                    }
                  >
                    {loadingDashboard
                      ? "…"
                      : formatCurrencyCompact(kpiStats.net)}
                  </span>
                  <span className="em-metric__hint"> · View all</span>
                </em>
              </div>
            </article>

            <article
              role="button"
              tabIndex={0}
              className={`em-metric em-metric--month em-metric--clickable${
                loadingDashboard ? " is-loading" : ""
              }`}
              title="View this month's expenses"
              onClick={() => {
                if (!sessionId) return;
                const ym = getCurrentYearMonth();
                window.location.href = `/AdminMain/ExpenseList?entry_type=CASH_OUT&year_month=${ym}`;
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                if (!sessionId) return;
                const ym = getCurrentYearMonth();
                window.location.href = `/AdminMain/ExpenseList?entry_type=CASH_OUT&year_month=${ym}`;
              }}
            >
              <span className="em-metric__icon" aria-hidden>
                <FaCalendarCheck />
              </span>
              <div className="em-metric__body">
                <span className="em-metric__label">This month</span>
                <strong title={formatCurrency(currentMonthData.total)}>
                  {loadingDashboard
                    ? "…"
                    : formatCurrencyCompact(currentMonthData.total)}
                </strong>
                <em>
                  {currentMonthData.count} txn · {kpiStats.monthShare.toFixed(0)}%
                  <span className="em-metric__hint"> · View</span>
                </em>
              </div>
            </article>

            <article
              className={`em-metric em-metric--pending${
                pendingCount > 0 ? " has-alert" : ""
              }${loadingDashboard ? " is-loading" : ""}`}
            >
              <span className="em-metric__icon" aria-hidden>
                <FaHourglassHalf />
              </span>
              <div className="em-metric__body">
                <span className="em-metric__label">Pending</span>
                <strong>{loadingDashboard ? "…" : pendingCount}</strong>
                <em>
                  {pendingCount > 0 ? (
                    <>
                      <FaExclamationTriangle aria-hidden /> Review
                    </>
                  ) : (
                    <>
                      <FaCheckCircle aria-hidden /> Clear
                    </>
                  )}
                  <button
                    type="button"
                    className="em-metric__action"
                    disabled={!sessionId}
                    onClick={() =>
                      setStatusFilter(pendingCount > 0 ? "PENDING" : "ALL")
                    }
                  >
                    {pendingCount > 0 ? "Filter" : "All"}
                  </button>
                </em>
              </div>
            </article>

            <article
              className={`em-metric em-metric--cat${
                loadingDashboard ? " is-loading" : ""
              }`}
            >
              <span className="em-metric__icon" aria-hidden>
                <FaTags />
              </span>
              <div className="em-metric__body">
                <span className="em-metric__label">Top category</span>
                <strong
                  className="em-metric__cat-name"
                  title={topCategory.name}
                >
                  {loadingDashboard ? "…" : topCategory.name}
                </strong>
                <em>
                  {loadingDashboard
                    ? "…"
                    : formatCurrencyCompact(topCategory.amount || 0)}
                  <span className="em-metric__dot" aria-hidden />
                  {topCategory.pct}%
                </em>
              </div>
            </article>
          </div>

          <div className="em-toolbar" role="toolbar" aria-label="Expense tools">
            <div className="em-toolbar__filters">
              <div className="em-search">
                <FaSearch className="em-search__icon" aria-hidden />
                <input
                  type="search"
                  className="em-search__input"
                  placeholder="Search category, party, remark…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={!sessionId}
                  aria-label="Search expenses"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="em-search__clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <FaTimes aria-hidden />
                  </button>
                ) : null}
              </div>
              <div className="em-status">
                <FaFilter className="em-status__icon" aria-hidden />
                <select
                  className="em-status__select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={!sessionId}
                  aria-label="Filter by status"
                >
                  <option value="ALL">All status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>
            <div className="em-toolbar__actions">
              <button
                type="button"
                className="em-tool-btn em-tool-btn--category"
                disabled={!sessionId}
                onClick={() => setIsManageCategoryOpen(true)}
              >
                <span className="em-tool-btn__icon" aria-hidden>
                  <FaTags />
                </span>
                <span className="em-tool-btn__text">Manage category</span>
              </button>
              <button
                type="button"
                className="em-tool-btn em-tool-btn--party"
                disabled={!sessionId}
                onClick={() => setIsManagePartyOpen(true)}
              >
                <span className="em-tool-btn__icon" aria-hidden>
                  <FaUsers />
                </span>
                <span className="em-tool-btn__text">Manage party</span>
              </button>
              <button
                type="button"
                className="em-tool-btn em-tool-btn--export"
                disabled={!sessionId}
                onClick={() => myToast.notify_warning("Export coming soon.")}
              >
                <span className="em-tool-btn__icon" aria-hidden>
                  <FaDownload />
                </span>
                <span className="em-tool-btn__text">Export</span>
              </button>
              <button
                type="button"
                className="em-tool-btn em-tool-btn--reports"
                disabled={!sessionId}
                onClick={() => {
                  window.location.href = "/AdminMain/ExpenseReport";
                }}
              >
                <span className="em-tool-btn__icon" aria-hidden>
                  <FaChartBar />
                </span>
                <span className="em-tool-btn__text">Reports</span>
              </button>
              <button
                type="button"
                className="em-tool-btn"
                disabled={!sessionId}
                title="Cash statement"
                onClick={() => {
                  window.location.href = "/AdminMain/StatementManager";
                }}
              >
                <span className="em-tool-btn__icon" aria-hidden>
                  <FaChartBar />
                </span>
                <span className="em-tool-btn__text">Statement</span>
              </button>
            </div>
          </div>

          <div className="em-grid">
            <section className="em-panel sr-section stur-section stur-section--compact">
              <div className="sr-section__head stur-section__head em-panel__head">
                <span className="stur-section__step" aria-hidden>
                  01
                </span>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Recent expenses</h2>
                  <p className="sr-section__sub">
                    Latest daybook cash-out entries
                  </p>
                </div>
                <div className="em-panel__actions">
                  <span className="em-panel__count">
                    {filteredExpenses.length}
                  </span>
                  <button
                    type="button"
                    className="em-btn-view-all"
                    disabled={!sessionId || loadingExpenses}
                    title="Open expense list"
                    onClick={() => {
                      if (!sessionId) return;
                      window.location.href =
                        "/AdminMain/ExpenseList?entry_type=CASH_OUT";
                    }}
                  >
                    View All
                  </button>
                </div>
              </div>
              <div className="sr-section__body">
                {loadingExpenses ? (
                  <div className="em-loading">
                    <MyLoader />
                  </div>
                ) : filteredExpenses.length === 0 ? (
                  <div className="em-empty">
                    <div className="em-empty__circle" aria-hidden>
                      <FaCreditCard />
                    </div>
                    <p className="em-empty__title">
                      {expenseList.length === 0
                        ? "No expenses found"
                        : "No matching expenses"}
                    </p>
                    <p className="em-empty__hint">
                      {expenseList.length === 0
                        ? "Add an expense for this session to get started."
                        : "Try another search or status filter."}
                    </p>
                    {expenseList.length === 0 ? (
                      <button
                        type="button"
                        className="sr-btn sr-btn--primary"
                        disabled={!sessionId}
                        onClick={() => setIsAddExpenseOpen(true)}
                      >
                        <FaPlus aria-hidden />
                        <span>Add first expense</span>
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="em-table-wrap">
                    <table className="em-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Category</th>
                          <th>Party</th>
                          <th>Mode</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredExpenses.map((item, index) => (
                          <tr key={item.entry_id || index}>
                            <td>{item.entry_date || "—"}</td>
                            <td>{item.category_name || "—"}</td>
                            <td>{item.party_name || "—"}</td>
                            <td>{item.payment_mode || "—"}</td>
                            <td className="em-table__amount">
                              {formatCurrency(item.amount)}
                            </td>
                            <td>
                              <span className={statusPillClass(item.status)}>
                                {statusLabel(item.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

            <section className="em-panel sr-section stur-section stur-section--compact">
              <div className="sr-section__head stur-section__head em-panel__head">
                <span className="stur-section__step" aria-hidden>
                  02
                </span>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Category breakdown</h2>
                  <p className="sr-section__sub">Spend share by category</p>
                </div>
                <span className="em-panel__count">
                  {categoryBreakdown.length}
                </span>
              </div>
              <div className="sr-section__body">
                {categoryBreakdown.length === 0 ? (
                  <div className="em-empty em-empty--soft">
                    <p className="em-empty__hint">No category data yet.</p>
                  </div>
                ) : (
                  <ul className="em-bars">
                    {categoryBreakdown.slice(0, 8).map((row) => (
                      <li key={row.name} className="em-bar">
                        <div className="em-bar__meta">
                          <strong>{row.name}</strong>
                          <span>
                            {formatCurrency(row.amount)} · {row.pct}%
                          </span>
                        </div>
                        <div className="em-bar__track" aria-hidden>
                          <div
                            className="em-bar__fill"
                            style={{ width: `${row.bar}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          {sessionMonthExpenses.length > 0 ? (
            <section className="em-panel em-panel--full em-months-panel sr-section stur-section stur-section--compact">
              <div className="sr-section__head stur-section__head em-panel__head">
                <span className="stur-section__step" aria-hidden>
                  03
                </span>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Session months</h2>
                  <p className="sr-section__sub">
                    Expense by month for this session
                  </p>
                </div>
                <div className="em-months-panel__meta">
                  <span className="em-panel__count">
                    {sessionMonthExpenses.length} months
                  </span>
                  <span className="em-months-panel__total">
                    {formatCurrency(
                      sessionMonthExpenses.reduce((s, m) => s + m.total, 0)
                    )}
                  </span>
                </div>
              </div>
              <div className="sr-section__body">
                <div className="em-months em-months--panel" role="list">
                  {(() => {
                    const currentYm = getCurrentYearMonth();
                    const ranked = sessionMonthExpenses.filter(
                      (m) => m.ym <= currentYm
                    );
                    const totals = ranked.map((m) => m.total);
                    const minSpend =
                      totals.length > 0 ? Math.min(...totals) : 0;
                    const maxSpend =
                      totals.length > 0 ? Math.max(...totals) : 0;
                    const span = maxSpend - minSpend;
                    return sessionMonthExpenses.map((m, idx) => {
                      const parts = String(m.label || "").split(" ");
                      const monthName = parts[0] || m.label;
                      const yearLabel = parts[1] || "";
                      const isUpcoming = m.ym > currentYm;
                      const ratio =
                        span <= 0
                          ? maxSpend > 0
                            ? 0.5
                            : 0
                          : (m.total - minSpend) / span;
                      const isLeast =
                        !isUpcoming && m.total === minSpend && span > 0;
                      const isMost =
                        !isUpcoming && m.total === maxSpend && maxSpend > 0;
                      return (
                        <article
                          key={m.ym}
                          className={`em-month-card${
                            m.isCurrent ? " is-current" : ""
                          }${m.count > 0 ? " has-spend" : " is-empty"}${
                            isLeast ? " is-least" : ""
                          }${isMost ? " is-most" : ""}${
                            isUpcoming ? " is-upcoming" : ""
                          }`}
                          role="listitem"
                          style={{
                            animationDelay: `${idx * 35}ms`,
                            ...(isUpcoming ? {} : spendHeatStyle(ratio)),
                          }}
                        >
                          <div className="em-month-card__top">
                            <div className="em-month-card__when">
                              <span className="em-month-card__month">
                                {monthName}
                              </span>
                              <span className="em-month-card__year">
                                {yearLabel}
                              </span>
                            </div>
                            {m.isCurrent ? (
                              <span className="em-month-card__badge">Now</span>
                            ) : isUpcoming ? (
                              <span className="em-month-card__badge em-month-card__badge--upcoming">
                                Upcoming
                              </span>
                            ) : isMost ? (
                              <span className="em-month-card__badge em-month-card__badge--most">
                                Most
                              </span>
                            ) : isLeast ? (
                              <span className="em-month-card__badge em-month-card__badge--least">
                                Least
                              </span>
                            ) : null}
                          </div>
                          <strong className="em-month-card__amount">
                            {formatCurrency(m.total)}
                          </strong>
                        </article>
                      );
                    });
                  })()}
                </div>
              </div>
            </section>
          ) : null}

          {(sessionMonthExpenses.length > 0 ||
            (dashboardData.by_party || []).length > 0) && (
            <section className="em-panel em-panel--full em-charts-panel sr-section stur-section stur-section--compact">
              <div className="sr-section__head stur-section__head em-panel__head">
                <span className="stur-section__step" aria-hidden>
                  04
                </span>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Expense graphs</h2>
                  <p className="sr-section__sub">
                    Month and party spend for this session
                  </p>
                </div>
              </div>
              <div className="sr-section__body">
                <div className="em-charts">
                  <article className="em-chart-card">
                    <div className="em-chart-card__head">
                      <span className="em-chart-card__icon" aria-hidden>
                        <FaCalendarAlt />
                      </span>
                      <div>
                        <strong>By month</strong>
                        <em>Session month expense</em>
                      </div>
                    </div>
                    <div className="em-chart-card__canvas">
                      {sessionMonthExpenses.length > 0 ? (
                        <Bar data={monthChartData} options={monthChartOptions} />
                      ) : (
                        <p className="em-chart-card__empty">No month data</p>
                      )}
                    </div>
                  </article>

                  <article className="em-chart-card">
                    <div className="em-chart-card__head">
                      <span className="em-chart-card__icon" aria-hidden>
                        <FaUsers />
                      </span>
                      <div>
                        <strong>By party</strong>
                        <em>Top parties by spend</em>
                      </div>
                    </div>
                    <div className="em-chart-card__canvas em-chart-card__canvas--donut">
                      {(dashboardData.by_party || []).length > 0 ? (
                        <Doughnut
                          data={partyChartData}
                          options={partyChartOptions}
                        />
                      ) : (
                        <p className="em-chart-card__empty">No party data</p>
                      )}
                    </div>
                  </article>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={(id, name, _status, startDate, endDate) => {
            setSessionId(id);
            setSessionName(name);
            setSessionStartDate(startDate || "");
            setSessionEndDate(endDate || "");
            setIsOpenSessionDialog(false);
          }}
        />
      ) : null}

      <MdlAddExpense
        isOpen={isAddExpenseOpen}
        onClose={() => setIsAddExpenseOpen(false)}
        sessionId={sessionId}
        sessionStartDate={sessionStartDate}
        sessionEndDate={sessionEndDate}
        onSuccess={() => {
          fetchExpenses();
          fetchDashboard();
        }}
      />
      <MdlManageDaybookMaster
        isOpen={isManageCategoryOpen}
        onClose={() => setIsManageCategoryOpen(false)}
        masterType="category"
      />
      <MdlManageDaybookMaster
        isOpen={isManagePartyOpen}
        onClose={() => setIsManagePartyOpen(false)}
        masterType="party"
      />
    </div>
  );
}
